import React, { useState, useEffect } from 'react';
import { battlePersonApi } from '../../api/battlePerson';
import { battlesApi } from '../../api/battles';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbInput from '../controls/HrnbInput';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbSearch from '../controls/HrnbSearch';
import '../../css/components/BattleChatAddParticipantModal.css';

/**
 * Модальное окно для добавления участника в чат баттла
 */
export default function BattleChatAddParticipantModal({
  isOpen,
  onClose,
  battleId,
  chatId,
  existingParticipantIds = [],
  onSuccess
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [battleParticipants, setBattleParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Логирование при изменении isOpen
  useEffect(() => {
    console.log('[BattleChatAddParticipantModal] isOpen изменился:', isOpen, {
      battleId,
      chatId,
      existingParticipantIdsCount: existingParticipantIds?.length || 0
    });
  }, [isOpen, battleId, chatId, existingParticipantIds]);

  // Загрузка участников баттла
  useEffect(() => {
    if (isOpen && battleId) {
      console.log('[BattleChatAddParticipantModal] Загрузка участников, battleId:', battleId);
      loadBattleParticipants();
    }
  }, [isOpen, battleId]);

  // Фильтрация участников по поиску
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredParticipants(battleParticipants);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = battleParticipants.filter(p => {
      const name = (p.name || '').toLowerCase();
      const nickname = (p.nickname || '').toLowerCase();
      const email = (p.email || '').toLowerCase();
      
      return name.includes(query) || nickname.includes(query) || email.includes(query);
    });

    setFilteredParticipants(filtered);
  }, [searchQuery, battleParticipants]);

  const loadBattleParticipants = async () => {
    try {
      setLoading(true);
      console.log('[BattleChatAddParticipantModal] Вызов battlePersonApi.list, battleId:', battleId);

      // Используем тот же API, что и на вкладке участников
      const data = await battlePersonApi.list({ battleId: battleId });
      const participantsArray = data?.content || [];

      console.log('[BattleChatAddParticipantModal] Получены участники:', participantsArray);
      console.log('[BattleChatAddParticipantModal] Тип данных:', Array.isArray(participantsArray) ? 'массив' : 'объект');

      setBattleParticipants(participantsArray);
      setFilteredParticipants(participantsArray);
    } catch (err) {
      console.error('[BattleChatAddParticipantModal] Failed to load participants:', err);
      console.error('[BattleChatAddParticipantModal] Ошибка details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError('Не удалось загрузить список участников: ' + (err.response?.status === 403 ? 'Нет доступа' : err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (participant) => {
    try {
      setAdding(true);
      setError(null);
      setSuccessMessage(null);

      console.log('[BattleChatAddParticipantModal] Добавление участника:', participant);
      console.log('[BattleChatAddParticipantModal] chatId:', chatId, 'profileId:', participant.profileId || participant.id);

      // Используем battlesApi.addParticipantToChat вместо chatsApi.addParticipant
      // Т.к. endpoint /api/chats/{chatId}/participants может возвращать 404
      await battlesApi.addParticipantToChat(battleId, participant.profileId || participant.id, participant.role || 'PARTICIPANT');

      setSuccessMessage(`Участник "${participant.name || participant.nickname}" добавлен в чат`);

      // Обновляем список (убираем добавленного)
      setBattleParticipants(prev => prev.filter(p => p.id !== participant.id));
      setFilteredParticipants(prev => prev.filter(p => p.id !== participant.id));

      // Уведомляем родителя об успехе
      if (onSuccess) {
        setTimeout(() => {
          onSuccess(participant);
        }, 1000);
      }
    } catch (err) {
      console.error('[BattleChatAddParticipantModal] Failed to add participant:', err);
      setError(
        err.response?.status === 409
          ? 'Участник уже в чате'
          : 'Не удалось добавить участника: ' + (err.response?.status === 404 ? 'Чат не найден' : err.message)
      );
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setError(null);
    setSuccessMessage(null);
    onClose();
  };

  // Участники, которые уже в чате
  const participantsInChat = battleParticipants.filter(p =>
    existingParticipantIds.includes(p.profileId || p.id)
  );

  // Участники, которых можно добавить (отфильтрованные по поиску)
  const availableParticipants = filteredParticipants.filter(p =>
    !existingParticipantIds.includes(p.profileId || p.id)
  );

  console.log('[BattleChatAddParticipantModal] Рендеринг списка:', {
    totalParticipants: battleParticipants.length,
    inChat: participantsInChat.length,
    availableToAdd: availableParticipants.length,
    searchQuery,
    filteredCount: filteredParticipants.length
  });

  return (
    <HrnbModal
      open={isOpen}
      onClose={handleClose}
      title="Добавить участника в чат"
      size="lg"
    >
      <div className="battle-chat-add-participant-modal">
        {/* Сообщения об ошибках/успехе */}
        {error && (
          <HrnbAlert variant="danger" onClose={() => setError(null)} className="mb-3">
            {error}
          </HrnbAlert>
        )}

        {successMessage && (
          <HrnbAlert variant="success" onClose={() => setSuccessMessage(null)} className="mb-3">
            {successMessage}
          </HrnbAlert>
        )}

        {/* Поиск */}
        <div className="mb-3">
          <HrnbSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск участника по имени, nickname или email"
            disabled={loading || adding}
            autoFocus
          />
        </div>

        {/* Статус */}
        <div className="participant-stats mb-3">
          <span className="text-muted">
            Можно добавить: {availableParticipants.length} из {battleParticipants.length} участников
          </span>
          {participantsInChat.length > 0 && (
            <span className="text-muted ml-3">
              | Уже в чате: {participantsInChat.length}
            </span>
          )}
        </div>

        {/* Список участников */}
        {loading ? (
          <div className="text-center py-4">
            <HrnbLoader size="sm" text="Загрузка участников..." />
          </div>
        ) : (
          <>
            {/* Доступные для добавления */}
            {availableParticipants.length > 0 && (
              <div className="mb-4">
                <h6 className="text-success mb-2">
                  ✅ Можно добавить ({availableParticipants.length})
                </h6>
                <div className="participants-list">
                  {availableParticipants.map((participant) => (
                    <div
                      key={participant.id}
                      className="participant-item"
                    >
                      <div className="participant-info">
                        <div className="participant-name">
                          {participant.name || participant.nickname || 'Аноним'}
                          {participant.role && (
                            <span className={`participant-role participant-role--${participant.role.toLowerCase()}`}>
                              {participant.role === 'ORGANIZER' ? '👑' : participant.role === 'JUDGE' ? '⚖️' : '🎤'}
                              {participant.role}
                            </span>
                          )}
                        </div>
                        {participant.nickname && (
                          <div className="participant-nickname text-muted">
                            @{participant.nickname}
                          </div>
                        )}
                        {participant.status && (
                          <div className="participant-status">
                            Статус: {participant.status}
                          </div>
                        )}
                      </div>
                      <HrnbButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleAddParticipant(participant)}
                        disabled={adding}
                      >
                        {adding ? <HrnbLoader size="sm" /> : 'Добавить'}
                      </HrnbButton>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Уже в чате */}
            {participantsInChat.length > 0 && (
              <div className="mb-2">
                <h6 className="text-muted mb-2">
                  🔒 Уже в чате ({participantsInChat.length})
                </h6>
                <div className="participants-list participants-list--disabled">
                  {participantsInChat.slice(0, 10).map((participant) => (
                    <div
                      key={participant.id}
                      className="participant-item participant-item--in-chat"
                    >
                      <div className="participant-info">
                        <div className="participant-name">
                          {participant.name || participant.nickname || 'Аноним'}
                          {participant.role && (
                            <span className={`participant-role participant-role--${participant.role.toLowerCase()}`}>
                              {participant.role === 'ORGANIZER' ? '👑' : participant.role === 'JUDGE' ? '⚖️' : '🎤'}
                              {participant.role}
                            </span>
                          )}
                        </div>
                        {participant.nickname && (
                          <div className="participant-nickname text-muted">
                            @{participant.nickname}
                          </div>
                        )}
                        {participant.status && (
                          <div className="participant-status">
                            Статус: {participant.status}
                          </div>
                        )}
                      </div>
                      <HrnbButton
                        variant="secondary"
                        size="sm"
                        disabled
                        title="Уже в чате"
                      >
                        ✓ В чате
                      </HrnbButton>
                    </div>
                  ))}
                  {participantsInChat.length > 10 && (
                    <div className="text-center py-2 text-muted">
                      ... и ещё {participantsInChat.length - 10} участников
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Нет участников */}
            {availableParticipants.length === 0 && participantsInChat.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted">
                  {searchQuery
                    ? 'Нет участников по вашему запросу'
                    : 'У баттла пока нет участников'}
                </p>
              </div>
            )}

            {/* Нет доступных, но есть в чате */}
            {availableParticipants.length === 0 && participantsInChat.length > 0 && !searchQuery && (
              <div className="text-center py-4">
                <p className="text-muted">
                  Все участники уже добавлены в чат
                </p>
              </div>
            )}
          </>
        )}

        {/* Кнопки управления */}
        <div className="modal-actions mt-4">
          <HrnbButton
            variant="secondary"
            onClick={handleClose}
            disabled={adding}
          >
            Закрыть
          </HrnbButton>
        </div>
      </div>
    </HrnbModal>
  );
}
